<template>
  <section class="form-section">
    <div class="section-header">
      <h2 class="section-title">Professional Experience</h2>
      <ContextMenu
        :is-authenticated="isAuthenticated"
        add-new-label="Add New Experience"
        add-from-master-label="Add from Profile"
        @add-new="addExperience"
        @add-from-master="handleAddFromMaster"
      />
    </div>
    
    <div v-for="(experience, index) in experienceList" :key="index" class="form-card">
      <div class="card-header">
        <h3 class="card-title">Experience {{ index + 1 }}</h3>
        <div class="flex items-center space-x-1 sm:space-x-2">
          <button
            v-if="isAuthenticated && isEntrySyncable(experience)"
            type="button"
            @click.stop="$emit('sync-entry', { entry: experience, entryType: 'experience' })"
            class="flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#000537]/25 text-[#000537] dark:border-indigo-400/40 dark:text-indigo-400 text-[10px] font-medium hover:bg-[#000537]/5 dark:hover:bg-indigo-400/10 transition-colors"
          >
            <svg class="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
            Save to profile
          </button>
          <button
            type="button"
            @click="moveExperienceUp(index)"
            class="btn-reorder"
            v-if="index > 0"
            title="Move up"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
            </svg>
          </button>
          <button
            type="button"
            @click="moveExperienceDown(index)"
            class="btn-reorder"
            v-if="index < experienceList.length - 1"
            title="Move down"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <button type="button" @click="removeExperience(index)" class="btn-remove" v-if="experienceList.length > 1" title="Delete">
            <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="form-grid">
        <div class="form-group">
          <label class="form-label">Job Title</label>
          <input 
            type="text" 
            :value="experience.jobTitle"
            @input="updateExperienceField(index, 'jobTitle', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'jobTitle') }]"
            placeholder="M&A Analyst"
            required
          />
          <div v-if="isFieldHighlighted(index, 'jobTitle')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Company</label>
          <input 
            type="text" 
            :value="experience.company"
            @input="updateExperienceField(index, 'company', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'company') }]"
            placeholder="Deutsche Bank"
            required
          />
          <div v-if="isFieldHighlighted(index, 'company')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Start Date</label>
          <div class="flex space-x-1 sm:space-x-2">
            <select
              :value="formatMonth(experience.startMonth)"
              @change="updateExperienceField(index, 'startMonth', $event.target.value)"
              class="form-input flex-1"
              required
            >
              <option value="">Month</option>
              <option value="01">Jan</option>
              <option value="02">Feb</option>
              <option value="03">Mar</option>
              <option value="04">Apr</option>
              <option value="05">May</option>
              <option value="06">Jun</option>
              <option value="07">Jul</option>
              <option value="08">Aug</option>
              <option value="09">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>
            <input 
              type="number" 
              :value="experience.startYear" 
              @input="updateExperienceField(index, 'startYear', $event.target.value)"
              class="form-input flex-1" 
              placeholder="2024"
              min="1950" 
              max="2030"
              required
            />
          </div>
        </div>
        <div class="form-group">
          <div class="flex items-start sm:items-center justify-between mb-1 flex-col sm:flex-row space-y-1 sm:space-y-0">
            <label class="form-label mb-0">End Date</label>
            <div class="flex items-center">
              <input 
                type="checkbox" 
                :id="`experience-current-${index}`" 
                :checked="experience.isCurrent"
                @change="updateExperienceField(index, 'isCurrent', $event.target.checked)"
                class="currently-checkbox"
              />
              <label :for="`experience-current-${index}`" class="currently-label">
                Currently working
              </label>
            </div>
          </div>
          <div v-if="!experience.isCurrent" class="flex space-x-1 sm:space-x-2">
            <select
              :value="formatMonth(experience.endMonth)"
              @change="updateExperienceField(index, 'endMonth', $event.target.value)"
              class="form-input flex-1"
              required
            >
              <option value="">Month</option>
              <option value="01">Jan</option>
              <option value="02">Feb</option>
              <option value="03">Mar</option>
              <option value="04">Apr</option>
              <option value="05">May</option>
              <option value="06">Jun</option>
              <option value="07">Jul</option>
              <option value="08">Aug</option>
              <option value="09">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>
            <input 
              type="number" 
              :value="experience.endYear" 
              @input="updateExperienceField(index, 'endYear', $event.target.value)"
              class="form-input flex-1" 
              placeholder="2024"
              min="1950" 
              max="2030"
              required
            />
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Company Description</label>
          <input 
            type="text" 
            :value="experience.companyDescription"
            @input="updateExperienceField(index, 'companyDescription', $event.target.value)"
            class="form-input"
            placeholder=" Deutsche Bank is Germany's leading investment bank"
          />
        </div>
        
        <div class="form-group">
          <label class="form-label">Location</label>
          <input 
            type="text" 
            :value="experience.location"
            @input="updateExperienceField(index, 'location', $event.target.value)"
            class="form-input"
            placeholder="Frankfurt, Germany"
          />
        </div>
        
        <div class="form-group col-span-full">
          <div class="flex items-center justify-between">
            <label class="form-label">Responsibilities & Achievements</label>
            <BulletOptimizer
              v-if="isAuthenticated"
              entry-type="experience"
              :bullets="experience.bulletPoints"
              :context="{
                job_title: experience.jobTitle,
                company: experience.company,
                company_description: experience.companyDescription,
                location: experience.location
              }"
              :cv-entry-id="experience.id || null"
              :master-entry-id="experience.masterExperienceEntryId || null"
              @apply="applyOptimizedBullets(index, $event)"
            />
          </div>
          <div class="space-y-1">
            <div
              v-for="(bulletPoint, bulletIndex) in experience.bulletPoints"
              :key="`experience-${index}-bullet-${bulletIndex}`"
              class="flex items-start space-x-1.5"
            >
              <span class="text-gray-400 dark:text-gray-500 text-xs mt-2 flex-shrink-0">•</span>
              <input
                type="text"
                :value="bulletPoint"
                @input="handleBulletInput(index, bulletIndex, $event.target.value)"
                :class="['form-input flex-1 min-w-0', { 'bullet-too-long': isBulletTooLong(bulletPoint) }]"
                :placeholder="bulletIndex === 0 ? 'Developed scalable web applications serving 1M+ users' : 'Add another responsibility...'"
              />
              <span
                v-if="isBulletTooLong(bulletPoint)"
                class="bullet-length-hint mt-2 flex-shrink-0"
                title="Longer bullets may wrap to a second line in the PDF"
              >{{ bulletPoint.trim().length }}/{{ maxBulletChars }}</span>
              <button
                v-if="experience.bulletPoints.length > 1 && bulletPoint.trim() !== ''"
                type="button"
                @click="removeBullet(index, bulletIndex)"
                class="text-gray-400 hover:text-red-500 transition-colors mt-2 flex-shrink-0"
                title="Remove bullet point"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Reference Documents Section (only for authenticated users) -->
      <ReferenceDocumentSection
        v-if="isAuthenticated"
        :entry-id="experience.id || null"
        entry-type="experience"
        :is-authenticated="isAuthenticated"
        @documents-updated="handleDocumentsUpdated"
      />
    </div>

    <!-- Master Data Modal -->
    <MasterDataModal 
      :is-open="showMasterDataModal"
      data-type="experience"
      @close="closeMasterDataModal"
      @items-selected="handleMasterDataSelection"
    />
  </section>
</template>

<script>
import MasterDataModal from './MasterDataModal.vue'
import ContextMenu from './ContextMenu.vue'
import ReferenceDocumentSection from './ReferenceDocumentSection.vue'
import BulletOptimizer from './BulletOptimizer.vue'
import { CV_CONFIG } from '../config/cvConfig.js'

export default {
  name: 'ExperienceForm',
  components: {
    MasterDataModal,
    ContextMenu,
    ReferenceDocumentSection,
    BulletOptimizer
  },
  props: {
    experienceList: {
      type: Array,
      required: true,
      default: () => [{
        jobTitle: '',
        company: '',
        startMonth: '',
        startYear: '',
        endMonth: '',
        endYear: '',
        location: '',
        companyDescription: '',
        bulletPoints: [''],
        isCurrent: false
      }]
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    syncableIds: {
      type: Array,
      default: () => []
    },
    highlightedFields: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:experienceList', 'field-changed', 'scroll-to-new', 'sync-entry'],
  data() {
    return {
      showMasterDataModal: false,
      maxBulletChars: CV_CONFIG.validation.maxBulletChars
    }
  },
  methods: {
    isBulletTooLong(bullet) {
      return (bullet || '').trim().length > this.maxBulletChars
    },

    isEntrySyncable(experience) {
      if (!experience.jobTitle || !experience.company) return false
      if (!experience.id) return true
      return this.syncableIds.includes(experience.id)
    },

    formatMonth(m) {
      if (m === null || m === undefined || m === '') return ''
      return String(m).padStart(2, '0')
    },

    updateExperienceField(index, field, value) {
      const updatedList = [...this.experienceList]
      updatedList[index] = { ...updatedList[index], [field]: value }
      this.$emit('update:experienceList', updatedList)
      this.$emit('field-changed')
    },

    addExperience() {
      const updatedList = [...this.experienceList, {
        jobTitle: '',
        company: '',
        startMonth: '',
        startYear: '',
        endMonth: '',
        endYear: '',
        location: '',
        companyDescription: '',
        bulletPoints: [''],
        isCurrent: false
      }]
      this.$emit('update:experienceList', updatedList)
      this.$emit('field-changed')
      
      // Emit scroll event with new entry index
      this.$nextTick(() => {
        this.$emit('scroll-to-new', { type: 'experience', index: updatedList.length - 1 })
      })
    },

    removeExperience(index) {
      if (this.experienceList.length > 1) {
        const updatedList = [...this.experienceList]
        updatedList.splice(index, 1)
        this.$emit('update:experienceList', updatedList)
        this.$emit('field-changed')
      }
    },

    moveExperienceUp(index) {
      if (index > 0) {
        const updatedList = [...this.experienceList]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index - 1, 0, item)
        this.$emit('update:experienceList', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'experience', index: index - 1 })
        })
      }
    },

    moveExperienceDown(index) {
      if (index < this.experienceList.length - 1) {
        const updatedList = [...this.experienceList]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index + 1, 0, item)
        this.$emit('update:experienceList', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'experience', index: index + 1 })
        })
      }
    },

    handleBulletInput(index, bulletIndex, value) {
      const updatedList = [...this.experienceList]
      const experience = { ...updatedList[index] }
      experience.bulletPoints = [...experience.bulletPoints]
      experience.bulletPoints[bulletIndex] = value
      
      // Add new bullet point if this is the last one and it's not empty
      if (value.trim() !== '' && bulletIndex === experience.bulletPoints.length - 1) {
        experience.bulletPoints.push('')
      }
      
      // Remove empty bullet points (except the last one)
      for (let i = experience.bulletPoints.length - 2; i >= 0; i--) {
        if (experience.bulletPoints[i].trim() === '' && i !== experience.bulletPoints.length - 1) {
          experience.bulletPoints.splice(i, 1)
        }
      }
      
      updatedList[index] = experience
      this.$emit('update:experienceList', updatedList)
      this.$emit('field-changed')
    },

    removeBullet(index, bulletIndex) {
      const updatedList = [...this.experienceList]
      const experience = { ...updatedList[index] }

      if (experience.bulletPoints.length > 1) {
        experience.bulletPoints = [...experience.bulletPoints]
        experience.bulletPoints.splice(bulletIndex, 1)
        updatedList[index] = experience
        this.$emit('update:experienceList', updatedList)
        this.$emit('field-changed')
      }
    },

    applyOptimizedBullets(index, newBullets) {
      const updatedList = [...this.experienceList]
      const experience = { ...updatedList[index] }
      // Keep the trailing empty bullet so the "add another" input stays available
      experience.bulletPoints = [...newBullets, '']
      updatedList[index] = experience
      this.$emit('update:experienceList', updatedList)
      this.$emit('field-changed')
    },

    handleAddFromMaster() {
      this.showMasterDataModal = true
    },

    handleMasterDataSelection(data) {
      // Convert master data items to experience format
      const newExperienceEntries = data.items.map(item => ({
        masterExperienceEntryId: item.id || null,
        jobTitle: item.job_title || '',
        company: item.company || '',
        location: item.location || '',
        startMonth: item.start_month || '',
        startYear: item.start_year || '',
        endMonth: item.end_month || '',
        endYear: item.end_year || '',
        companyDescription: item.company_description || '',
        bulletPoints: item.base_bullet_points?.length ? item.base_bullet_points : [''],
        isCurrent: item.is_current || false
      }))

      // Add the new entries to the existing list
      const updatedList = [...this.experienceList, ...newExperienceEntries]
      this.$emit('update:experienceList', updatedList)
      this.$emit('field-changed')

      // Scroll to the first new entry
      if (newExperienceEntries.length > 0) {
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { 
            type: 'experience', 
            index: this.experienceList.length 
          })
        })
      }
    },

    closeMasterDataModal() {
      this.showMasterDataModal = false
    },

    isFieldHighlighted(index, fieldName) {
      const highlightedEntry = this.highlightedFields.find(entry => entry.index === index)
      return highlightedEntry && highlightedEntry.fields.includes(fieldName)
    },

    handleDocumentsUpdated(data) {
      // This method handles when reference documents are updated
      console.log(`Experience entry ${data.entryId} now has ${data.documentCount} reference documents`)
      this.$emit('field-changed') // Trigger any auto-save or preview updates
    }
  }
}
</script>

<style scoped>
/* Field Highlighting Styles */
.field-needs-review {
  @apply border-2 border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20;
}

.field-needs-review:focus {
  @apply border-red-600 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-800;
}

.review-badge {
  @apply inline-flex items-center text-xs font-medium text-red-600;
}

/* Subtle warning for bullets that would wrap to a second line in the PDF */
.bullet-too-long {
  @apply border-amber-400 dark:border-amber-500;
}

.bullet-length-hint {
  @apply text-[10px] text-amber-500 dark:text-amber-400 tabular-nums;
}
/* No additional styles needed - ContextMenu component handles all styling */
</style>